import React, { useMemo } from 'react';
import { Box, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { toast } from 'react-toastify';
import useInvRole from '../../../hooks/useInvRole';
import { INV_ACTIONS } from '../../../utils/inventoryRoles';
import { filterToggleSx } from '../../../theme';
import { fadeInSx } from '../pharmacyStyles';
import OpdExistingPatientPanel from '../opd/OpdExistingPatientPanel';
import DirectDispenseFlow from '../direct/DirectDispenseFlow';

const MODES = {
  existing: 'existing',
  direct: 'direct',
};

function readMode(search) {
  const mode = new URLSearchParams(search).get('mode');
  return mode === MODES.direct ? MODES.direct : MODES.existing;
}

function readAnonymous(search) {
  const raw = new URLSearchParams(search).get('anonymous');
  if (raw === '0' || raw === 'false') return false;
  if (raw === '1' || raw === 'true') return true;
  return true;
}

export default function DirectDispensesTab() {
  const history = useHistory();
  const location = useLocation();
  const { url } = useRouteMatch();
  const { can } = useInvRole();

  const mode = readMode(location.search);
  const initialAnonymous = readAnonymous(location.search);

  const canWalkIn = can(INV_ACTIONS.DIRECTDISPENSE_WALKIN);
  const canQuickRegister = can(INV_ACTIONS.DIRECTDISPENSE_QUICKREGISTER);
  const canDirect = canWalkIn || canQuickRegister;

  const modeOptions = useMemo(() => ([
    { value: MODES.existing, label: 'Existing Patient', allowed: true },
    { value: MODES.direct, label: 'Direct', allowed: canDirect },
  ]), [canDirect]);

  function setMode(next) {
    if (next === MODES.direct && !canDirect) {
      toast.error('You do not have permission to start a direct dispense');
      return;
    }
    const params = new URLSearchParams(location.search);
    if (next === MODES.existing) {
      params.delete('mode');
      params.delete('anonymous');
    } else {
      params.set('mode', MODES.direct);
      if (!params.has('anonymous')) params.set('anonymous', '1');
    }
    const qs = params.toString();
    history.replace(`${url}${qs ? `?${qs}` : ''}`);
  }

  return (
    <Box sx={fadeInSx}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 2.5 }}>
        <ToggleButtonGroup
          value={mode}
          exclusive
          onChange={(_, v) => v && setMode(v)}
          size="small"
          sx={{ ...filterToggleSx, height: 40 }}
        >
          {modeOptions.map((opt) => (
            <ToggleButton
              key={opt.value}
              value={opt.value}
              disabled={!opt.allowed}
            >
              {opt.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        {mode === MODES.direct && (
          <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
            Use Anonymous walk-in for OTC, or turn it off to register a patient on the bill.
          </Typography>
        )}
      </Box>

      {mode === MODES.existing && (
        <OpdExistingPatientPanel dispensePath={`${url}/dispense`} />
      )}

      {mode === MODES.direct && canDirect && (
        <DirectDispenseFlow
          key={`direct-${initialAnonymous ? '1' : '0'}`}
          initialAnonymous={initialAnonymous}
          embedded
          onBack={() => setMode(MODES.existing)}
        />
      )}

      {mode === MODES.direct && !canDirect && (
        <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>
          You can look up existing patients, but you do not have permission to start walk-in or quick-register dispenses.
        </Typography>
      )}
    </Box>
  );
}
